import type {
  GetBrandDTO,
  GetVisualDirectionResponse,
  VisualDirectionItem,
} from "@nlc/shared-types";

import { useQuery } from "@tanstack/react-query";
import { ImagesIcon } from "lucide-react";
import { useEffect, useMemo, useState } from "react";

import { MediaPreviewModalProvider } from "@/components/media-preview-modal/MediaPreviewModalProvider";
import { useMediaPreviewModal } from "@/components/media-preview-modal/useMediaPreviewModal";
import { Skeleton } from "@/components/skeleton";
import { VideoThumbnail } from "@/components/video-thumbnail/VideoThumbnail";
import { visualDirectionKeys } from "@/hooks/queryKeys.ts";
import apiClient from "@/lib/api/client.ts";

import { SectionHeader } from "../SectionHeader";

const PAGE_SIZE = 30;

const MOODBOARD_GRID_CLASS = "grid grid-cols-2 gap-md @md:grid-cols-3";

type VisualDirectionTabProps = {
  brand: Pick<GetBrandDTO, "id">;
};

export function VisualDirectionTab(props: VisualDirectionTabProps) {
  return (
    <MediaPreviewModalProvider usePreview>
      <VisualDirectionTabContent {...props} />
    </MediaPreviewModalProvider>
  );
}

function VisualDirectionTabContent({ brand }: VisualDirectionTabProps) {
  const { data, isLoading } = useVisualDirection(brand.id);
  const items = useMemo(() => data?.items ?? [], [data]);
  const { openModal, setMediaItems } = useMediaPreviewModal();

  useEffect(() => {
    setMediaItems(
      items.map((item) => ({
        extension: item.extension,
        id: String(item.id),
        name: item.name,
        url: item.url,
      })),
    );
  }, [items, setMediaItems]);

  return (
    <div className="@container flex flex-col gap-xl">
      <SectionHeader Icon={ImagesIcon} title="Visual direction" />

      {isLoading ? (
        <div
          aria-label="Loading..."
          className={MOODBOARD_GRID_CLASS}
          role="status"
        >
          {Array.from({ length: 6 }).map((_, index) => (
            <Skeleton className="aspect-square rounded-2xl" key={index} />
          ))}
        </div>
      ) : items.length === 0 ? (
        <p className="py-xl text-center text-sm text-text-quaternary">
          No visual direction available
        </p>
      ) : (
        <div className={MOODBOARD_GRID_CLASS}>
          {items.map((item) => (
            <MoodboardTile item={item} key={item.id} onPress={openModal} />
          ))}
        </div>
      )}
    </div>
  );
}

function MoodboardTile({
  item,
  onPress,
}: {
  item: VisualDirectionItem;
  onPress: (id: string) => void;
}) {
  const isVideo = item.mimeType.startsWith("video/");
  const [imgSrc, setImgSrc] = useState(() => deriveThumbnailUrl(item.url));

  useEffect(() => {
    setImgSrc(deriveThumbnailUrl(item.url));
  }, [item.url]);

  return (
    <button
      className="aspect-square overflow-hidden rounded-2xl"
      onClick={() => onPress(String(item.id))}
      title={item.name}
      type="button"
    >
      {isVideo ? (
        <VideoThumbnail className="size-full" fill src={item.url} />
      ) : (
        <img
          alt={item.name}
          className="size-full object-cover"
          onError={() => {
            if (imgSrc !== item.url) setImgSrc(item.url);
          }}
          src={imgSrc}
        />
      )}
    </button>
  );
}

function useVisualDirection(brandId: number) {
  return useQuery<GetVisualDirectionResponse>({
    queryFn: async () =>
      await apiClient
        .url(`/brands/${brandId}/visual-direction`)
        .query({ limit: PAGE_SIZE, offset: 0 })
        .get()
        .json(),
    queryKey: visualDirectionKeys.byBrandId(brandId),
  });
}

function deriveThumbnailUrl(url: string): string {
  return url.replace(/(\.[\w-]+)$/i, "-thumbnail$1");
}
